import React from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Grid, 
  Link, 
  Divider, 
  IconButton, 
  useTheme,
  Stack
} from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GitHubIcon from '@mui/icons-material/GitHub';

const Footer: React.FC = () => {
  const theme = useTheme();
  const currentYear = new Date().getFullYear();
  
  const footerLinks = [
    {
      title: 'Product',
      links: [
        { name: 'Features', href: '#features' },
        { name: 'Pricing', href: '#pricing' },
        { name: 'Pay and Start', href: '/sign_in' },
        { name: 'Updates', href: '#announcements' }
      ]
    },
    {
      title: 'Company',
      links: [
        { name: 'About Us', href: '#' },
        { name: 'Team', href: '#team' },
        { name: 'Careers', href: '#' },
        { name: 'Contact', href: '#' }
      ]
    },
    {
      title: 'Resources',
      links: [
        { name: 'Documentation', href: '#' },
        { name: 'Tutorials', href: '#' },
        { name: 'Blog', href: '#' },
        { name: 'Support', href: '#' }
      ]
    },
    {
      title: 'Legal',
      links: [
        { name: 'Privacy Policy', href: '#' },
        { name: 'Terms of Service', href: '#' },
        { name: 'Cookie Policy', href: '#' },
        { name: 'GDPR', href: '#' }
      ]
    }
  ];
  
  const socialLinks = [
    { icon: <FacebookIcon />, href: 'https://facebook.com' },
    { icon: <TwitterIcon />, href: 'https://twitter.com' },
    { icon: <LinkedInIcon />, href: 'https://linkedin.com' },
    { icon: <GitHubIcon />, href: 'https://github.com' }
  ];
  
  return (
    <Box 
      component="footer" 
      sx={{ 
        py: 6,
        backgroundColor: theme.palette.mode === 'dark' 
          ? theme.palette.background.paper 
          : '#f5f5f5',
        borderTop: `1px solid ${theme.palette.divider}`
      }}
    >
      <Container maxWidth="lg">
        <Stack 
          direction={{ xs: 'column', md: 'row' }} 
          spacing={{ xs: 4, md: 2 }}
          justifyContent="space-between"
        >
          {footerLinks.map((section) => (
            <Box key={section.title} sx={{ minWidth: { md: '150px' } }}>
              <Typography variant="h6" color="text.primary" gutterBottom>
                {section.title}
              </Typography>
              <Box>
                {section.links.map((link) => (
                  <Link
                    key={link.name}
                    href={link.href}
                    color="text.secondary"
                    sx={{ 
                      display: 'block', 
                      mb: 1,
                      textDecoration: 'none',
                      '&:hover': {
                        color: theme.palette.primary.main,
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    {link.name}
                  </Link>
                ))}
              </Box>
            </Box>
          ))}
          
          <Box sx={{ minWidth: { md: '200px' } }}>
            <Typography variant="h6" color="text.primary" gutterBottom>
              Web Starter
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Package your static frontend files and backend startup files into an executable that runs through a browser.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {socialLinks.map((link, index) => (
                <IconButton
                  key={index}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  sx={{ 
                    color: theme.palette.text.secondary,
                    '&:hover': {
                      color: theme.palette.primary.main
                    }
                  }}
                >
                  {link.icon}
                </IconButton>
              ))}
            </Box>
          </Box>
        </Stack>
        
        <Divider sx={{ my: 4 }} />
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' } }}>
          <Typography variant="body2" color="text.secondary">
            © {currentYear} Web Starter. All rights reserved.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Made with ❤️ for developers worldwide
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
